// src/components/Delegation/DelegationList.js
import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import DelegationModal from "./DelegationModal";

const PAGE_SIZE = 10;

const DelegationList = () => {
  const [delegations, setDelegations] = useState([]);
  const [users, setUsers] = useState([]); // Employee list
  const [documentTypes, setDocumentTypes] = useState([]); // Document type list
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Load data when component mounts
  useEffect(() => {
    fetchMainData();
    fetchLookupData();
  }, []);

  // Fetch main delegation list
  const fetchDelegations = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.delegations);
      setDelegations(res.data || []);
    } catch (error) {
      console.error("❌ Error loading delegations:", error);
    }
  };

  // Fetch lookup data (Users & Document Types) for the Modal
  const fetchLookupData = async () => {
    try {
      // Parallel API calls for performance
      const [resUsers, resDocs] = await Promise.all([
        axiosClient.get(`${API_ENDPOINTS.delegations}/users-lookup`),
        axiosClient.get(`${API_ENDPOINTS.delegations}/document-types`)
      ]);
      setUsers(resUsers.data || []);
      setDocumentTypes(resDocs.data || []);
    } catch (error) {
      console.error("❌ Error loading lookup data:", error);
    }
  };

  const fetchMainData = () => {
    fetchDelegations();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this delegation record?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.delegations}/${id}`);
      setDelegations(delegations.filter(d => d.id !== id));
    } catch (error) {
      alert(error.response?.data || "Unable to delete");
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingDelegation) {
        await axiosClient.put(`${API_ENDPOINTS.delegations}/${editingDelegation.id}`, data);
      } else {
        await axiosClient.post(API_ENDPOINTS.delegations, data);
      }
      await fetchDelegations(); // Reload list after saving
      setShowModal(false);
      setEditingDelegation(null);
    } catch (error) {
      alert("Error saving data!");
    }
  };

  // Filter display data
  const filteredData = delegations.filter(d =>
    (d.delegatorName || "").toLowerCase().includes(search.toLowerCase()) ||
    (d.delegateeName || "").toLowerCase().includes(search.toLowerCase()) ||
    (d.function || "").toLowerCase().includes(search.toLowerCase()) ||
    (d.documentTypeName || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentData = filteredData.slice(startIndex, startIndex + PAGE_SIZE);

  const thStyle = { backgroundColor: "#f8f9fc", color: "#4e73df", fontWeight: "bold", fontSize: "0.85rem", textTransform: "uppercase" };

  return (
    <div className="container-fluid py-4">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h4 mb-0 text-gray-800 fw-bold">DELEGATION MANAGEMENT</h1>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h6 className="m-0 font-weight-bold text-primary">Approval Delegation List</h6>

          <div className="d-flex align-items-center gap-2">
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm ps-4"
                style={{ width: "200px", borderRadius: "20px" }}
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
              <i className="fas fa-search position-absolute text-muted" style={{ left: "12px", top: "8px", fontSize: "12px" }}></i>
            </div>

            <button
              className="btn btn-sm btn-success px-3 rounded-pill shadow-sm fw-bold"
              onClick={() => { setEditingDelegation(null); setShowModal(true); }}
            >
              <i className="fas fa-plus me-1"></i> Add New
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={{ ...thStyle, width: "80px" }}>No.</th>
                  <th style={thStyle}>Delegator</th>
                  <th style={thStyle}>Delegatee</th>
                  <th style={thStyle}>Function</th>
                  <th style={thStyle}>Document Type</th>
                  <th className="text-center" style={{ ...thStyle, width: "150px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? currentData.map((d, index) => (
                  <tr key={d.id}>
                    <td className="text-center text-muted small">{startIndex + index + 1}</td>
                    <td className="fw-bold text-dark">{d.delegatorName}</td>
                    <td className="text-primary">{d.delegateeName}</td>
                    <td><span className="badge bg-light text-dark border">{d.function}</span></td>
                    <td>{d.documentTypeName}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary border-0 rounded-circle shadow-sm"
                          style={{ width: "32px", height: "32px", padding: "0" }}
                          onClick={() => { setEditingDelegation(d); setShowModal(true); }}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger border-0 rounded-circle shadow-sm"
                          style={{ width: "32px", height: "32px", padding: "0" }}
                          onClick={() => handleDelete(d.id)}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">No delegation data found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
              <span className="small text-muted fw-bold">Page {currentPage} / {totalPages}</span>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage - 1)}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </li>
                  <li className="page-item active">
                    <button className="page-link border-0 shadow-sm mx-1 rounded-circle bg-primary text-white">{currentPage}</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage + 1)}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Modal with lookup data from API */}
      <DelegationModal
        show={showModal}
        initialData={editingDelegation}
        users={users}
        documentTypes={documentTypes}
        onClose={() => { setShowModal(false); setEditingDelegation(null); }}
        onSave={handleSave}
      />
    </div>
  );
};

export default DelegationList;